/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ApplicationState } from 'store';
import { fetchUserQuestion } from 'store/modules/bidding_chat/actions';
import {
  BiddingChatState,
  BiddingChatUserSavedQuestionContentTypes,
} from 'store/modules/bidding_chat/types';

import { Overlay, Panel } from './styles';

export interface BidderReportSelection {
  biddingAnalysis: boolean;
  bidderConsideration: boolean;
  questions: BiddingChatUserSavedQuestionContentTypes[];
}

interface BiddingSummaryBidderReportDrawerProps {
  isGenerating?: boolean;
  onClose: () => void;
  onGenerate: (selection: BidderReportSelection) => void;
}

const BiddingSummaryBidderReportDrawer: React.FC<BiddingSummaryBidderReportDrawerProps> = ({
  isGenerating = false,
  onClose,
  onGenerate,
}) => {
  const dispatch = useDispatch();

  const { user_saved: userSaved, request } = useSelector<ApplicationState, BiddingChatState>(
    (state) => state.session.bidding_chat,
  );

  const [withAnalysis, setWithAnalysis] = useState(true);
  const [withConsideration, setWithConsideration] = useState(true);
  const [withQuestions, setWithQuestions] = useState(true);
  const [showQuestions, setShowQuestions] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const [expandedQuestionId, setExpandedQuestionId] = useState<number | null>(null);

  const questions: BiddingChatUserSavedQuestionContentTypes[] = useMemo(
    () => (Array.isArray(userSaved.questions) ? userSaved.questions : []),
    [userSaved.questions],
  );

  const isLoadingQuestions = Boolean(request.fetch_user_question?.request?.loading);
  const hasQuestions = questions.length > 0;

  useEffect(() => {
    dispatch(fetchUserQuestion());
  }, [dispatch]);

  useEffect(() => {
    setSelectedQuestionIds(questions.map((question) => question.id));
  }, [questions]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const toggleQuestion = (id: number) =>
    setSelectedQuestionIds((prev) =>
      prev.includes(id) ? prev.filter((current) => current !== id) : [...prev, id],
    );

  const selection: BidderReportSelection = {
    biddingAnalysis: withAnalysis,
    bidderConsideration: withConsideration,
    questions: withQuestions
      ? questions.filter((question) => selectedQuestionIds.includes(question.id))
      : [],
  };

  const isEmptySelection =
    !withAnalysis && !withConsideration && (!withQuestions || selectedQuestionIds.length === 0);

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <div className="drawer-content">
          <div className="drawer-head">
            <p className="drawer-title">O relatório deve conter:</p>
            <button type="button" className="drawer-close" onClick={onClose} aria-label="Fechar">
              <span className="material-icons">close</span>
            </button>
          </div>

          <div className="report-option custom-control custom-checkbox">
            <input
              type="checkbox"
              className="custom-control-input"
              id="bidder-report-analysis"
              checked={withAnalysis}
              onChange={(event) => setWithAnalysis(event.target.checked)}
            />
            <label className="custom-control-label" htmlFor="bidder-report-analysis">
              Análise do edital
            </label>
          </div>

          <div className="report-option custom-control custom-checkbox">
            <input
              type="checkbox"
              className="custom-control-input"
              id="bidder-report-consideration"
              checked={withConsideration}
              onChange={(event) => setWithConsideration(event.target.checked)}
            />
            <label className="custom-control-label" htmlFor="bidder-report-consideration">
              Análise e Considerações do Licitante
            </label>
          </div>

          <div className="report-option custom-control custom-checkbox">
            <input
              type="checkbox"
              className="custom-control-input"
              id="bidder-report-questions"
              checked={withQuestions && hasQuestions}
              disabled={!hasQuestions}
              onChange={(event) => setWithQuestions(event.target.checked)}
            />
            <label className="custom-control-label" htmlFor="bidder-report-questions">
              Pergunte ao Edital{' '}
              {hasQuestions && (
                <span className="report-option-count">
                  ({selectedQuestionIds.length} de {questions.length}{' '}
                  {questions.length === 1 ? 'pergunta selecionada' : 'perguntas selecionadas'})
                </span>
              )}
            </label>
          </div>

          {isLoadingQuestions && !hasQuestions && (
            <p className="questions-empty">Carregando suas perguntas salvas…</p>
          )}

          {!isLoadingQuestions && !hasQuestions && (
            <p className="questions-empty">
              Você ainda não tem perguntas salvas no Pergunte ao Edital.
            </p>
          )}

          {hasQuestions && withQuestions && (
            <>
              <button
                type="button"
                className="questions-toggle"
                onClick={() => setShowQuestions((prev) => !prev)}
              >
                Escolher perguntas
                <span className="material-icons">
                  {showQuestions ? 'expand_less' : 'expand_more'}
                </span>
              </button>

              {showQuestions && (
                <div className="questions-list">
                  {questions.map((question) => {
                    const inputId = `bidder-report-question-${question.id}`;
                    const isExpanded = expandedQuestionId === question.id;

                    return (
                      <div className="question-card" key={question.id}>
                        <div className="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id={inputId}
                            checked={selectedQuestionIds.includes(question.id)}
                            onChange={() => toggleQuestion(question.id)}
                          />
                          <label className="custom-control-label" htmlFor={inputId}>
                            <span className="sr-only">Incluir esta pergunta no relatório</span>
                          </label>
                        </div>

                        <p className={`question-text ${isExpanded ? '' : 'is-clamped'}`}>
                          {question.content}
                        </p>

                        <button
                          type="button"
                          className="question-expand"
                          onClick={() => setExpandedQuestionId(isExpanded ? null : question.id)}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Recolher pergunta' : 'Expandir pergunta'}
                        >
                          <span className="material-icons">
                            {isExpanded ? 'expand_less' : 'expand_more'}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div className="drawer-footer">
          <button
            type="button"
            className="drawer-generate"
            onClick={() => onGenerate(selection)}
            disabled={isEmptySelection || isGenerating}
          >
            {isGenerating ? 'Gerando PDF…' : 'Gerar arquivo'}
          </button>
        </div>
      </Panel>
    </Overlay>
  );
};

export default BiddingSummaryBidderReportDrawer;
